import React from 'react';
import PropTypes from 'prop-types';
import s from './ReportItem.module.scss';
import Image from '../Image';

import { t } from '../../i18n';
import Icon from '../Icon';
import classNames from 'classnames';
import Button from '../Button/Button';
import Status from '../Status/Status';
import Link from 'next/link';
import { formatAuthorNames } from '../../utils/helpers';
import TagList from '../TagList/TagList';
import Highlighter from 'react-highlight-words';

const ReportItem = ({
    image,
    articleTitle: title,
    subtitle,
    authors = [],
    publishedDate,
    isLarge = false,
    url,
    fullReportFile = {},
    dataSetFile = {},
    isSeenByUser,
    tags = [],
    showTags = false,
    countries = [],
    category,
    modifiers = [],
    searchWords = [],
}) => {
    const isCard = modifiers.includes('Card');

    const classes = classNames(
        s.Root,
        modifiers.map((modifier) => s[`Root--${modifier}`]),
        {
            [s.Large]: isLarge,
            [s.IsNewReport]: !isSeenByUser,
            [s.HasImage]: !!image && !isCard,
        }
    );

    const authorNames = formatAuthorNames(authors.map((author) => author.name));

    const combinedTags = countries.concat(tags);
    const categoryTag = category?.label ? (
        <span className={s.CategoryTag}>{category.label}</span>
    ) : null;

    return (
        <div className={classes}>
            {!!url && (
                <Link href={url} className={s.Link}>
                    <span className="sr-only">
                        {t('general.read')} {title}
                    </span>
                </Link>
            )}
            <div className={s.Container}>
                {image && !isCard && (
                    <div className={s.Image}>
                        <Image {...image} sizes={['200px']} />
                    </div>
                )}
                <div className={s.Content}>
                    {!isSeenByUser && !isCard && (
                        <div className={s.Status}>
                            <Status
                                modifier="Thin"
                                statusColor="Pink"
                                statusText={t('reportItem.newReport')}
                            />
                        </div>
                    )}
                    <div className={s.TopContent}>
                        <h3 className={s.Title}>
                            {searchWords.length ? (
                                <Highlighter
                                    highlightClassName={s.Highlight}
                                    searchWords={searchWords}
                                    autoEscape={true}
                                    textToHighlight={title || ''}
                                />
                            ) : (
                                title
                            )}
                        </h3>
                        {isCard ? (
                            <div className={s.CardByline}>
                                {!isSeenByUser && (
                                    <span className={s.CardStatus}>
                                        <Status
                                            modifier="Thin"
                                            statusColor="Pink"
                                            statusText={t(
                                                'reportItem.newReport'
                                            )}
                                        />
                                    </span>
                                )}
                                {!!authorNames && <span>{authorNames}</span>}
                                {!!publishedDate && (
                                    <span>{publishedDate}</span>
                                )}
                            </div>
                        ) : (
                            <div className={s.Byline}>
                                <span
                                    className={
                                        s.Author
                                    }>{`${t('reportItem.reportBy')} ${authorNames}`}</span>
                                <span className={s.Date}>{publishedDate}</span>
                            </div>
                        )}
                    </div>
                    {subtitle && !isCard && (
                        <p className={s.Subtitle}>{subtitle}</p>
                    )}
                    {isCard && (!!category?.label || !!combinedTags.length) && (
                        <div className={s.Tags}>
                            {combinedTags.length ? (
                                <TagList
                                    tags={combinedTags}
                                    before={categoryTag}
                                />
                            ) : (
                                categoryTag
                            )}
                        </div>
                    )}
                    {!isCard && showTags && !!combinedTags.length && (
                        <div className={s.Tags}>
                            <TagList tags={combinedTags} />
                        </div>
                    )}
                    {!isLarge && fullReportFile.fileUrl && (
                        <a
                            href={fullReportFile.fileUrl}
                            className={s.Download}
                            target="_blank"
                            rel="noopener noreferrer">
                            <Icon name="Download" />
                        </a>
                    )}
                </div>
                {isLarge && (fullReportFile.fileUrl || dataSetFile.fileUrl) && (
                    <div className={s.DownloadButtons}>
                        {fullReportFile.fileUrl && (
                            <Button
                                title={t('reportItem.downloadFullReport')}
                                url={fullReportFile.fileUrl}
                                modifier="SecondaryDarkGrey"
                                size="Small"
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        )}
                        {dataSetFile.fileUrl && (
                            <Button
                                title={t('reportItem.downloadDataSet')}
                                url={dataSetFile.fileUrl}
                                modifier="SecondaryDarkGrey"
                                size="Small"
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

ReportItem.propTypes = {};

export default ReportItem;
